/**
 * Safra (Banco Safra) brand style configuration — colors, typography, and
 * prompt rules extracted from the client's "Safra.pptx" (slide master
 * colorScheme + fontScheme, layout placeholder geometry, via the OOXML).
 *
 * Mirrors the shape of `cavendishStyleConfig.ts` / `maFinancialStyleConfig.ts`.
 * When Safra style is active the prompt below is appended to the system
 * prompt and the color/theme constants are used as renderer defaults.
 *
 * Fidelity note: colors come from the master-linked colorScheme "safra color 3"
 * (ppt/theme/theme1.xml); fonts from fontScheme "Safra" (Arial for titles AND
 * body). Slide size is 780×540 pt. Title/content/footnote geometry is taken
 * from the "Call Out (texto)" content layout. Raster logos live on the Call
 * Out layouts (not the slide master), so new content slides should use
 * contentLayoutName "Call Out (texto)".
 */

import type { ThemeColors } from "../../types";

// ---------------------------------------------------------------------------
// Color palette (6-digit hex, no '#') — from the "safra color 3" colorScheme
// ---------------------------------------------------------------------------

export const SAFRA_COLORS = {
  navy: "00003C", // accent1 — Safra deep navy (primary brand / emphasis)
  blue: "547BA6", // accent2 — muted blue
  crimson: "96143C", // accent3 — dark red
  gold: "C0964D", // accent4 — gold / tan
  green: "4D5C4D", // accent5 — muted green
  slate: "3A5268", // accent6 — slate blue

  ink: "000000", // dk1 / windowText lastClr — titles & body text (black)
  white: "FFFFFF", // lt1 / window
  darkGray: "404040", // dk2
  gray: "9E9E9E", // lt2

  ruleGray: "7F7F7F", // hlink gray — 1pt rule / separator lines
  altRow: "F2F2F2", // light neutral for alternate-row table shading
} as const;

/**
 * Drop-in ThemeColors override when Safra style is active. Values map 1:1 to
 * the Safra slide-master colorScheme + fontScheme.
 */
export const SAFRA_THEME_COLORS: ThemeColors = {
  dark1: SAFRA_COLORS.ink, // #000000 — Text1
  dark2: SAFRA_COLORS.darkGray, // #404040 — Text2
  light1: SAFRA_COLORS.white, // #FFFFFF — Background1
  light2: SAFRA_COLORS.gray, // #9E9E9E — Background2
  accent1: SAFRA_COLORS.navy, // #00003C
  accent2: SAFRA_COLORS.blue, // #547BA6
  accent3: SAFRA_COLORS.crimson, // #96143C
  accent4: SAFRA_COLORS.gold, // #C0964D
  accent5: SAFRA_COLORS.green, // #4D5C4D
  accent6: SAFRA_COLORS.slate, // #3A5268
  hyperlink: "7F7F7F",
  followedHyperlink: "6D0017",
  majorFont: "Arial",
  minorFont: "Arial",
};

/**
 * Ordered series palette — matches the template charts, which use the theme
 * accents in order (accent1 leads, then accent2, ...).
 */
export const SAFRA_CHART_SERIES_PALETTE = [
  SAFRA_COLORS.navy,
  SAFRA_COLORS.blue,
  SAFRA_COLORS.crimson,
  SAFRA_COLORS.gold,
  SAFRA_COLORS.green,
  SAFRA_COLORS.slate,
];

// ---------------------------------------------------------------------------
// Slide dimensions & placeholder positions (from "Call Out (texto)")
// ---------------------------------------------------------------------------

export const SAFRA_SLIDE_DIMENSIONS = { width: 780, height: 540 } as const;

export const SAFRA_POSITIONS = {
  title: { left: 25.0, top: 21.8, width: 730.8, height: 33.9 },
  contentStart: { left: 24.2, top: 105.5 },
  footnote: { left: 25.1, top: 512.2, width: 545.0, height: 8.5 },
} as const;

// Authoritative layout names from the template master (Portuguese).
export const SAFRA_LAYOUT_NAMES = [
  "Capa - sem logo",
  "1_Capa - sem logo",
  "1_Capa -  com logo",
  "Seção",
  "Seção c/ subitem",
  "Agenda",
  "Agenda com Subseção",
  "Agenda com anexo",
  "1_Agenda com Subseção",
  "Call Out - Base",
  "Call Out (c/1)",
  "Call Out (c/ 2)",
  "Call Out (c/ 4)",
  "Call Out (c/ 2) horizontal",
  "1_Call Out (c/ 2) horizontal",
  "1_Call Out (c/ 3)",
  "Call Out (c/ 3 vertical)",
  "Call Out (c/ 4_1 horizontal)",
  "Call Out (c/ 4_1 horizontal cima)",
  "Call Out (c/ 5)",
  "Call Out (c/ 6)",
  "Call Out (c/ 5) 1/2 (c/ 4) direita",
  "Call Out (c/ 5) 1/2 (c/ 4) esquerda",
  "2_Call Out (c/ 6)",
  "Call Out (c/ 7)",
  "1_Call Out (c/ 7)",
  "Call Out  simples (c/ 1)",
  "Call Out  simples (c/ 2)",
  "Call Out  simples (c/ 2 vertical)",
  "Call Out  simples (c/ 4)",
  "Call Out  simples (c/ 3)",
  "Call Out  simples (c/ 3 horizontal)",
  "Call Out  simples (c/ 3 vertical)",
  "Call Out  simples (c/ 1) direta",
  "Call Out  simples (c/ 2) direita",
  "Call Out  simples (c/ 2 vertical) direita",
  "Call Out  simples (c/ 4) direita",
  "Call Out  simples (c/ 3) direita",
  "Call Out  simples (c/ 3 horizontal) direita",
  "Call Out  simples (c/ 3 vertical) direita",
  "Call Out (c/ 3) horizontal",
  "Call Out (c/ 4) horizontal.",
  "Call Out (c/ 5) horizontal.",
  "Call Out (c/ 3) horizontal.",
  "Call Out Picture (c/ 2)",
  "Call Out Picture (c/ 3)",
  "Call Out Picture (c/ 4)",
  "Call Out Picture (c/ 5)",
  "Call Out (texto)",
  "Slide de Título",
] as const;

// ---------------------------------------------------------------------------
// Prompt text — appended to the system prompt when Safra style is active
// ---------------------------------------------------------------------------

export const SAFRA_STYLE_PROMPT = `

## SAFRA BRAND STYLE OVERRIDE (active — apply these rules to ALL output)

CRITICAL FONT RULE: You MUST include \`fontFamily\` on EVERY shape you create or modify — even when editing existing text. This OVERRIDES the general rule about matching the existing presentation's fonts. Safra uses a single Latin typeface throughout:
- Titles, headings, body, bullets, tables, charts: \`"Arial"\`
NEVER omit fontFamily when Safra style is active. If a shape already has the wrong font, include fontFamily to correct it.

### Color palette (from the Safra master colorScheme "safra color 3")
Use ONLY these Safra brand colors. Do NOT use arbitrary hex values.

**Theme mapping:**
- Text1 / dark1: Black #${SAFRA_COLORS.ink}
- Text2 / dark2: Dark gray #${SAFRA_COLORS.darkGray}
- Background1 / light1: White #${SAFRA_COLORS.white}
- Background2 / light2: Gray #${SAFRA_COLORS.gray}
- Accent1: Deep navy #${SAFRA_COLORS.navy}
- Accent2: Blue #${SAFRA_COLORS.blue}
- Accent3: Crimson #${SAFRA_COLORS.crimson}
- Accent4: Gold #${SAFRA_COLORS.gold}
- Accent5: Green #${SAFRA_COLORS.green}
- Accent6: Slate #${SAFRA_COLORS.slate}

**Usage:**
- **Primary brand / emphasis**: Deep navy #${SAFRA_COLORS.navy}
- **Text / ink**: Black #${SAFRA_COLORS.ink}
- **Lines & borders**: Gray #${SAFRA_COLORS.ruleGray}
- **Background / shading**: Light gray #${SAFRA_COLORS.altRow}
- **Chart series palette** (in order): ${SAFRA_CHART_SERIES_PALETTE.map((c) => `#${c}`).join(", ")}
  Lead with deep navy (#${SAFRA_COLORS.navy}) as the first/primary series color.

### Typography (authoritative — from the Safra "Call Out (texto)" layout)
Slide dimensions: 780 × 540 pt.

**Placeholder positions:**
- **Title**: left=25.0, top=21.8, width=730.8, height=33.9 — "Arial" 22pt, black (#${SAFRA_COLORS.ink}), left-aligned
- **Content area**: starts at left=24.2, top=105.5
- **Footnote / footer**: left=25.1, top=512.2, width=545.0, height=8.5 — "Arial" ~7pt, black

**Content text hierarchy (from the master — "Arial", black #${SAFRA_COLORS.ink}, dense IB sizing ~11pt body):**
- Keep body text compact (≈11pt); sub-levels smaller. Sentence case, single-spaced, left-justified.
- For an emphasized section heading, use bold; bullets otherwise inherit black text.

### Charts (Safra style)
- Font: "Arial", minimum 8pt
- **Primary data color**: deep navy (#${SAFRA_COLORS.navy}); multi-series uses the series palette above in order
- Data labels: positioned outside bars/columns (inside for stacked)
- Gridlines: minimal — none by default; only when no data labels are shown
- Legend: bottom-center placement (only when needed for multi-series)
- No 3D effects, gradients, or graphic fills

### Tables (Safra style)
- Header row: deep navy (#${SAFRA_COLORS.navy}) fill with white text, bold
- Body: white fill, black "Arial" text (10pt)
- Alternate-row shading: light gray (#${SAFRA_COLORS.altRow})
- Borders: thin gray (#${SAFRA_COLORS.ruleGray}); align numeric columns consistently

### Shapes & layout (Safra style)
- Separate content with SHARP rectangular blocks — NOT rounded shapes
- Use thin gray (#${SAFRA_COLORS.ruleGray}) rule lines for separation when needed
- Connectors: solid gray (#${SAFRA_COLORS.ruleGray}) lines
- Page numbers / footer: ~7pt, black, "Arial"
- Prefer layout "Call Out (texto)" for freeform content slides so brand furniture (logo/footer) is inherited.

REFERENCE PRECEDENTS: The attached "Safra.pptx" contains the current visual standard. Match the closest relevant precedent for covers, section dividers, agendas, call-out grids, and text pages. Prefer its title hierarchy, spacing, and footer treatment over inventing a generic layout.

**TEXT FITTING RULE**: Keep labels and item descriptions SHORT (max 6-8 words each). Text boxes are narrow; long descriptions overflow. Use concise labels, not full sentences.
`;
